import React from "react";
import {
  PieChart,
  Pie,
  ResponsiveContainer,
  Tooltip,
  Legend,
  Cell,
} from "recharts";
import _ from "lodash";
import styles from "./style.module.scss";
import { CHART_COLORS } from "../../../../../services/colorUtil";
import { formatChartNumber } from "../chartNumberFormat";

interface SeriesData {
  label: string;
  x: (string | number)[];
  y: number[];
}

interface ChartArtifacts {
  meta: {
    xlabel: string;
    ylabel: string;
    title?: string;
  };
  series: SeriesData[];
}

interface PieChartViewProps {
  artifacts: ChartArtifacts;
}

const PieChartView: React.FC<PieChartViewProps> = ({ artifacts }) => {
  if (!artifacts?.series?.length) {
    return <div className={styles.noDataMessage}>No chart data available</div>;
  }

  const series = _.zipWith(
    _.get(artifacts, "series[0].x", []) || _.get(artifacts, "series[0].data", []),
    _.get(artifacts, "series[0].y", []) || _.get(artifacts, "series[0].labels", []),
    (name, value) => ({ name, value })
  );

  if (_.size(series) === 0) {
    return <div className={styles.noDataMessage}>No chart data available</div>;
  }

  return (
    <div
    className={styles.chartContainer}
    >

      <ResponsiveContainer width="100%" height={400}>
        <PieChart>
          <defs>
            {CHART_COLORS.map((color, i) => (
              <linearGradient id={`grad-${i}`} key={i} x1="0" y1="0" x2="1" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.9} />
                <stop offset="95%" stopColor={color} stopOpacity={0.5} />
              </linearGradient>
            ))}
          </defs>

          <Pie
            data={series}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={130}
            innerRadius={60}
            paddingAngle={3}
            stroke="#fff"
            strokeWidth={2}
            animationDuration={800}
          >
            {series.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={`url(#grad-${index % CHART_COLORS.length})`}
              />
            ))}
          </Pie>

          <Tooltip
            formatter={(value: number, name: string) => [
              formatChartNumber(Number(value), "fixed2"),
              name,
            ]}
            contentStyle={{
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              backgroundColor: "rgba(255,255,255,0.9)",
              boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
            }}
          />
          <Legend
            verticalAlign="top"
            align="center"
            wrapperStyle={{
              fontSize: "14px",
              marginTop: "20px",
              color: "#475569",
            }}
          />
          <text x="100%" y="95%" textAnchor="end" dominantBaseline="auto" className={styles.disclaimer}>* AI Generated Graph</text>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PieChartView;